/**
 * @ComponentFor HeroBlockViewModel
 */
import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@glitz/react';
import { isCompact } from 'Shared/Viewport';
import connect from 'Shared/connect';
import { HEIGHT as DESKTOP_HEADER_HEIGHT } from 'SiteLayout/Header/DesktopHeader';
import { BLACK_TOP_MOBILE_HEIGHT, BLACK_TOP_DESKTOP_HEIGHT } from 'SiteLayout/Header';
import Viewport from 'Shared/Viewport';
import { HEIGHT as MOBILE_HEIGHT } from 'SiteLayout/Header/MobileHeader';
import { QUICKSEARCH_HEIGHT } from 'Search/QuickSearch';
import * as style from 'Shared/Style';
import { LinkButton } from 'Shared/TextButtons';
import HeroBlockViewModel from './HeroBlockViewModel.type';
import HeroBlockItemViewModel from './HeroItemBlockViewModel.type';
import { Down32x64 } from 'Shared/Icons/ArrowSwirl';
import { setImageBackground, Preset } from 'Shared/image-background';
import { LeftUri, RightUri } from 'Shared/Icons/ArrowCircle';
import { selectDeviceImage } from 'Shared/utils';
import VideoPlayer from 'Shared/Video/VideoPlayer'
import { H2, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import SwiperList from 'Shared/SwiperList';

type HeroBlockType = HeroBlockViewModel;

const MARGIN_TOP_HERO = '170px'

const Base = styled.div({
  height: '100vh',
  width: '100%',
  display: 'flex',
  position: 'relative',
  overflow: 'hidden',
});

const SliderWrap = styled.div({
  height: '100vh',
  width: '100%',
})

const SliderItemWrap = styled.div({
  pointerEvents: 'none',
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyItems: 'center',
  alignItems: 'center',
})

const SliderItemBackground = styled.div({
  position: 'absolute',
  left: '0',
  top: '0',
  height: '100vh',
  width: '100%',
  transition: { property: ['transform, width, margin'], duration: 300, timingFunction: 'ease' },
})

const ItemContent = styled.div({
  height: '100vh',
  width: '100%',
  alignItems: 'center',
  display: 'grid',
  gridTemplateRows: '1fr auto 1fr'
});

const ItemContentWrap = styled.div({
  gridRowStart: 2,
  display: 'flex',
  flexDirection: 'column'
})

const ItemHeading = styled(H2, {
  margin: {
    y: 0,
    x: 0,
  },
  textAlign: 'center',
  lineHeight: '100%',
  userSelect: 'none',
  color: style.colors.monochrome.white,
  width: '100%',
  transition: { property: ['all'], duration: 600, timingFunction: 'ease' },
  transform: 'translateX(0)'
})

const ItemLabel = styled.span({
  lineHeight: 1,
  fontSize: '14px',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  color: style.colors.monochrome.white,
  marginTop: 0,
  display: 'block',
  marginBottom: '16px',
  textAlign: 'center',
  zIndex: 1,
})

const ItemBottomWrap = styled.div({
  textAlign: 'center',
  gridRowStart: 3,
  height: '100%'
})

const ItemButtonWrap = styled.div({
  display: 'flex',
  justifyContent: 'center',
})

const ItemButton = styled(LinkButton, {
  cursor: 'pointer',
  pointerEvents: 'auto',
})

const Swirl = styled(Down32x64, {
  color: style.colors.monochrome.white,
  marginBottom: '16px'
})

const SlideNavContainer = styled.div({
  height: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  left: '0',
  top: '0',
  justifyContent: 'space-between',
  overflow: 'hidden',
});

const SlideNavBase = styled.div({
  zIndex: 1,
  height: '100vh',
  marginTop: MARGIN_TOP_HERO,
  width: 'calc(((100% - 32px) / 12) * 2 + 16px)',
  opacity: 0,
  pointerEvents: 'auto',
})

const SlideNavLeft = styled(SlideNavBase, {
  cursor: `url("${LeftUri}") 24 24, pointer`,
})

const SlideNavRight = styled(SlideNavBase, {
  cursor: `url("${RightUri}") 24 24, pointer`,
})

const getOffset = (isMobile: boolean) => {
  const gridGap = isMobile ? 8 : 16;
  const sideGap = isMobile ? 12 : 16;
  const sideMultiplier = isMobile ? 1 : 2;
  const totalWidth = window.innerWidth; //document.documentElement.clientWidth;
  const offset = Math.round(((totalWidth - (2 * sideGap)) / 12) * sideMultiplier + gridGap);
  return offset;
};


const HeroBlock = connect((state) => {
  return {
    currentBreakpoint: state.currentBreakpoint,
  };
})((props: HeroBlockType & { currentBreakpoint: number }) => {
  const isMobile = isCompact(props.currentBreakpoint);
  const { items } = props;
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [realSlideIndex, setRealSlideIndex] = useState(0);
  const [currentSlideBgElement, setCurrentSlideBg] = useState<HTMLElement>(null);
  const [nextSlideBgElement, setNextSlideBg] = useState<HTMLElement>(null);
  const [offSet, setOffset] = useState(getOffset(isMobile));
  const swiperRef = useRef<any>(null);

  /* function getSideOffset() {
    console.log("doc: " + document);
    if(typeof document === undefined || typeof document.documentElement === undefined) {
      return 0;
    }

    const gridGap = isMobile ? 8 : 16;
    const sideGap = isMobile ? 12 : 16;
    const totalWidth = document.documentElement.clientWidth;
    const offset = Math.round(((totalWidth - (2 * sideGap)) / 12) * 2 + gridGap);
    return offset;
  } */

  const updateOffset = () => {
    setOffset(getOffset(isMobile));
  };

  useEffect(() => {
    window.addEventListener("resize", updateOffset);
    return () => window.removeEventListener("resize", updateOffset);
  });

  useEffect(() => {
    if (swiperRef.current !== null) {
      setActiveSlideIndex(swiperRef.current.activeIndex);
    }
  }, [activeSlideIndex]);

  useEffect(() => {
    if (swiperRef.current !== null) {
      setRealSlideIndex(swiperRef.current.realIndex);
    }
  }, [realSlideIndex]);

  function mouseLeaveLeft() {
    if (nextSlideBgElement !== null) {
      nextSlideBgElement.style.transform = 'translateX(0)'
      setNextSlideBg(null);
    }

    if (currentSlideBgElement !== null) {
      currentSlideBgElement.style.marginLeft = '';
      currentSlideBgElement.style.width = '';
      setCurrentSlideBg(null);
    }
  }

  function mouseLeaveRight() {
    if (nextSlideBgElement !== null) {
      nextSlideBgElement.style.transform = 'translateX(0)'
      setNextSlideBg(null);
    }

    if (currentSlideBgElement !== null) {
      currentSlideBgElement.style.width = '';
      setCurrentSlideBg(null);
    }
  }

  function getHeading(item: HeroBlockItemViewModel) {
    if (isMobile && item.mobileHeading) {
      if (item.mobileHeadingExtraRow) {
        return <>{item.mobileHeading}<br />{item.mobileHeadingExtraRow}</>
      }

      return (
        <>{item.mobileHeading}</>
      );
    }

    if (item.headingExtraRow) {
      return <>{item.heading}<br />{item.headingExtraRow}</>
    }

    return (
      <>{item.heading}</>
    );
  }

  const originalItemCount = items.length;
  const firstItem = items[0];
  const lastItem = items[items.length - 1];
  const adjustedItems: HeroBlockItemViewModel[] = originalItemCount > 1 ? [lastItem, ...items, firstItem] : items;
  const itemCount = adjustedItems.length;
  const gridGap = isMobile ? 8 : 16;
  return (
    <Viewport>
      {(isCompact) => (
        <Base
          css={{
            marginTop: isCompact
              ? -(QUICKSEARCH_HEIGHT + MOBILE_HEIGHT)
              : -(isCompact ? MOBILE_HEIGHT : DESKTOP_HEADER_HEIGHT) -
              (isCompact ? BLACK_TOP_MOBILE_HEIGHT : BLACK_TOP_DESKTOP_HEIGHT),
          }}
        >
          <SliderWrap>
            <SwiperList
              isCompact={isCompact}
              slidesPerView={1}
              spaceBetween={0}
              showNav={false}
              height='100%'
              freeMode={false}
              loop={false}
              rewind={true}
              grabCursor={false}
              sliderRef={swiperRef}
              initialSlide={1}
              onSlideChange={(swiper) => {
                if (swiper !== null) {
                  if (swiper.realIndex === 0) {
                    swiper.slideTo((itemCount - 2), 300, true);
                    setRealSlideIndex(itemCount - 2);
                  } else if (swiper.realIndex === (itemCount - 1)) {
                    swiper.slideTo(1, 300, true);
                    setRealSlideIndex(1);
                  } else {
                    setRealSlideIndex(swiper.realIndex);
                  }
                }

              }}
              cssMode={true}
            >
              {adjustedItems.map((item, index) => {
                let shiftLeft = realSlideIndex !== index && ((realSlideIndex === 0 && index === (itemCount - 1)) || (index < realSlideIndex && realSlideIndex < itemCount));
                let shiftRight = realSlideIndex !== index && ((realSlideIndex === 0 && index > 0) || (realSlideIndex == (itemCount - 1) && index === 0) || (index > realSlideIndex));

                return (
                  <styled.Div
                    key={index}
                    className='swiper-slide'
                    style={{
                      ...(shiftLeft && {
                        zIndex: 2,
                      })
                    }}
                  >
                    <SliderItemWrap>
                      <SliderItemBackground
                        className='sld-bg'
                        css={{
                          ...((item.backgroundVideoSources?.length ?? 0) < 1 && {
                            background: {
                              image: setImageBackground(selectDeviceImage(item.backgroundImage, item.backgroundImageSmallScreen, isCompact)?.url, Preset.FullWidth),
                              size: 'cover',
                              positionX: 'center',
                              positionY: 'center',
                            }
                          }),
                        }}
                      >
                        {item.backgroundVideoSources && item.backgroundVideoSources.length > 0 && (
                          <VideoPlayer
                            autoPlay
                            loop
                            muted
                            coverContent
                            playsInline
                            isMobile={isCompact}
                            sources={item.backgroundVideoSources}
                          />
                        )}
                      </SliderItemBackground>

                      <ItemContent key={index}>
                        <ItemContentWrap>
                          {item.label && <ItemLabel css={{ color: item.textColor, }}>{item.label}</ItemLabel>}
                          {item.heading && <ItemHeading
                            upperCase
                            fontSize={ResponsiveSize.D96_M40}
                            css={{
                              ...(!shiftLeft && !shiftRight && {
                                paddingLeft: `${offSet + gridGap}px`,
                                paddingRight: `${offSet + gridGap}px`,
                                boxSizing: 'border-box',
                              }),
                              ...(shiftLeft && {
                                textAlign: 'right',
                                transform: `translateX(${offSet - gridGap}px)`,
                                opacity: '0.5',
                                whiteSpace: 'nowrap',
                                direction: 'rtl',
                              }),
                              ...(shiftRight && {
                                textAlign: 'left',
                                transform: `translateX(-${offSet - gridGap}px)`,
                                opacity: '0.5',
                                whiteSpace: 'nowrap',
                                direction: 'rtl',
                              }),
                              color: item.textColor,
                            }}
                          >
                            {getHeading(item)}
                          </ItemHeading>}
                        </ItemContentWrap>
                        <ItemBottomWrap>
                          {item.label && item.heading && <Swirl />}
                          <ItemButtonWrap>
                            {item.button && item.button.url && (
                              <ItemButton
                                to={item.button.url}
                                text={item.button.text}
                                title={item.button.text}
                              />
                            )}
                          </ItemButtonWrap>
                        </ItemBottomWrap>

                      </ItemContent>
                    </SliderItemWrap>
                  </styled.Div>
                );
              })}
            </SwiperList>
          </SliderWrap>
          {!isCompact &&
            <SlideNavContainer>
              <SlideNavLeft
                onClick={() => {
                  const idx = swiperRef.current.realIndex <= 0 ? (itemCount - 1) : (swiperRef.current.realIndex - 1);
                  swiperRef.current.slideToLoop(idx);
                  setActiveSlideIndex(swiperRef.current.activeIndex);
                  mouseLeaveLeft();
                }}
                {...{
                  onMouseEnter: () => {
                    const currentIndex = swiperRef.current.activeIndex;

                    const prevSlide = swiperRef.current.slides[currentIndex === 0 ? (itemCount - 1) : (currentIndex - 1)] as HTMLElement;
                    var nextSlideBg = prevSlide.querySelector('.sld-bg') as HTMLElement;
                    nextSlideBg.style.transform = `translateX(${offSet}px)`
                    setNextSlideBg(nextSlideBg);
                    const currentSlide = swiperRef.current.slides[swiperRef.current.activeIndex] as HTMLElement;
                    var currentSlideBg = currentSlide.querySelector('.sld-bg') as HTMLElement;
                    currentSlideBg.style.width = `calc(100% - ${offSet}px)`;
                    currentSlideBg.style.marginLeft = `${offSet}px`;
                    setCurrentSlideBg(currentSlideBg);
                  },
                  onMouseLeave: () => {
                    mouseLeaveLeft();
                  },
                }}
              />
              <SlideNavRight
                onClick={() => {
                  const idx = swiperRef.current.realIndex >= (itemCount - 1) ? 0 : (swiperRef.current.realIndex + 1);
                  swiperRef.current.slideToLoop(idx);
                  setActiveSlideIndex(swiperRef.current.activeIndex);
                  mouseLeaveRight();
                }}
                {...{
                  onMouseEnter: () => {
                    const currentIndex = swiperRef.current.activeIndex;
                    const nextSlide = swiperRef.current.slides[currentIndex === (itemCount - 1) ? 0 : (currentIndex + 1)] as HTMLElement;
                    var nextSlideBg = nextSlide.querySelector('.sld-bg') as HTMLElement;
                    nextSlideBg.style.transform = `translateX(-${offSet}px)`

                    setNextSlideBg(nextSlideBg);
                    const currentSlide = swiperRef.current.slides[swiperRef.current.activeIndex] as HTMLElement;
                    var currentSlideBg = currentSlide.querySelector('.sld-bg') as HTMLElement;
                    currentSlideBg.style.width = `calc(100% - ${offSet}px)`;
                    setCurrentSlideBg(currentSlideBg);
                  },
                  onMouseLeave: () => {
                    mouseLeaveRight();
                  },
                }}
              />
            </SlideNavContainer>
          }
        </Base>
      )}
    </Viewport>
  );
})

export default HeroBlock;
